import { useState, useEffect, useRef, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { getPedidos, getBlingContatos } from '../../utils/storage'
import { formatTelefone } from '../../utils/fmt'
import { Search, UserCheck } from 'lucide-react'

const VENDEDORAS = ['Daíze', 'Natália', 'Sócio']

const FORMAS_PAGAMENTO = [
  { value: 'pix',             label: 'Pix',               icon: '⚡' },
  { value: 'dinheiro',        label: 'Dinheiro',          icon: '💵' },
  { value: 'credito',         label: 'Cartão de crédito', icon: '💳' },
  { value: 'debito',          label: 'Cartão de débito',  icon: '🏧' },
]

const PRAZOS_PAGAMENTO = [
  { value: 'ato',       label: 'Tudo no ato do pedido'              },
  { value: '50_50',     label: '50% no pedido + 50% na entrega'     },
  { value: 'custom',    label: 'Personalizado'                       },
]

function Field({ label, error, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {error && <p className="error-msg">{error.message}</p>}
    </div>
  )
}

// Extrai clientes únicos de pedidos Mini Deck + contatos Bling
// Pedidos Mini Deck têm prioridade (dados mais completos); Bling preenche o resto
function getClientesCadastrados() {
  const vistos  = new Map()   // chave normalizada → cliente

  // 1. Pedidos Mini Deck (mais recente primeiro, já vêm ordenados)
  for (const p of getPedidos()) {
    const c = p.cliente
    if (!c?.nome) continue
    const chave = (c.documento?.replace(/\D/g,'') || c.nome).trim().toLowerCase()
    if (!vistos.has(chave)) vistos.set(chave, { ...c, _origem: 'minideck' })
  }

  // 2. Contatos do Bling (só adiciona os que ainda não apareceram)
  for (const c of getBlingContatos()) {
    if (!c?.nome) continue
    const doc   = (c.numeroDocumento || '').replace(/\D/g,'')
    const chave = (doc || c.nome).trim().toLowerCase()
    if (vistos.has(chave)) continue   // já existe via Mini Deck

    // Mapeia endereço: prefere entrega, depois geral, depois cobrança
    const end = c.endereco?.entrega || c.endereco?.geral || c.endereco?.cobranca || {}
    const docDigits = doc.replace(/\D/g,'')
    const tipoDoc   = docDigits.length === 14 ? 'CNPJ' : 'CPF'

    vistos.set(chave, {
      nome:           c.nome,
      contato:        c.celular || c.telefone || '',
      email:          c.email   || '',
      documento:      docDigits,
      tipoDocumento:  tipoDoc,
      rua:            end.endereco  || '',
      numero:         end.numero    || '',
      complemento:    end.complemento || '',
      bairro:         end.bairro    || '',
      cidade:         end.municipio || end.cidade || '',
      uf:             end.uf        || '',
      cep:            end.cep       || '',
      _origem: 'bling',
    })
  }

  return [...vistos.values()]
}

// Remove acentos para comparação: "André" → "Andre"
function sem(str) {
  return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

function BuscaCliente({ onSelecionar }) {
  const [query,    setQuery]    = useState('')
  const [focado,   setFocado]   = useState(false)
  const [focusIdx, setFocusIdx] = useState(-1)
  const inputRef                = useRef(null)
  const clientes                = useMemo(() => getClientesCadastrados(), [])

  // Filtragem inline — sem useEffect/useEffect, sempre sincronizado com query
  const q          = sem(query.trim())
  const qDigitos   = q.replace(/\D/g, '')
  const resultados = q
    ? clientes.filter(c =>
        sem(c.nome).includes(q) ||
        sem(c.contato).includes(q) ||
        (qDigitos.length >= 3 && (c.documento || '').replace(/\D/g,'').includes(qDigitos))
      ).slice(0, 8)
    : []

  const aberto = focado && resultados.length > 0

  function selecionar(c) {
    onSelecionar(c)
    setQuery('')
    setFocado(false)
    inputRef.current?.blur()
  }

  function handleKeyDown(e) {
    if (!aberto) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx(i => Math.min(i + 1, resultados.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setFocusIdx(i => Math.max(i - 1, 0)) }
    if (e.key === 'Enter' && focusIdx >= 0) { e.preventDefault(); selecionar(resultados[focusIdx]) }
    if (e.key === 'Escape') { setQuery(''); setFocado(false) }
  }

  function handleChange(e) {
    setQuery(e.target.value)
    setFocusIdx(-1)
  }

  if (clientes.length === 0) return null

  return (
    <div className="relative mb-2">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocado(true)}
          onBlur={() => setTimeout(() => setFocado(false), 150)}
          placeholder="Buscar cliente já cadastrado…"
          className="input pl-9 pr-4 bg-[#FFFDE8] border-[#F5C200]/60 placeholder:text-slate-400 focus:border-[#F5C200]"
        />
      </div>

      {aberto && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {resultados.map((c, i) => (
            <li key={i}>
              <button
                type="button"
                onMouseDown={() => selecionar(c)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                  ${focusIdx === i ? 'bg-[#FFF9C2]' : 'hover:bg-slate-50'}`}
              >
                <UserCheck size={14} className="text-[#CC8800] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{c.nome}</p>
                    {c._origem === 'bling' && (
                      <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-500 border border-blue-100">
                        Bling
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 truncate">
                    {[c.contato, c.cidade].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Bloco de forma de pagamento ────────────────────────────────────────────
// Toggle "Dividir em 2 formas" — se OFF: 1 forma + valor pendente na entrega
// (comportamento antigo, funciona pra pagamento único ou entrada+entrega
// simples). Se ON: 2 slots (entrada + saldo) com valor + forma + obs cada.
// Cobre casos como "entrada cartão + saldo pix na entrega", "50% maquineta
// + 50% espécie", etc. Soma dos 2 slots é mostrada e comparada com o total
// como feedback visual — não bloqueia salvar (soma pode não bater se tem
// desconto ou frete separado).

function fmtBR(v) {
  const limpo = String(v || '').replace(/[^\d,]/g, '')
  const [int, ...dec] = limpo.split(',')
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (dec.length ? ',' + dec.join('').slice(0,2) : '')
}
function parseBR(v) {
  const n = Number(String(v || '').replace(/\./g,'').replace(',','.'))
  return isNaN(n) ? 0 : n
}

function PagamentoBloco() {
  const { register, watch, setValue, formState: { errors } } = useFormContext()
  const dividido = watch('pagamentoDividido')
  const moveis   = watch('moveis') || []
  const precoFinalRaw = watch('precoFinal') || ''
  const precoFinalNum = parseBR(precoFinalRaw)
  const totalBruto    = moveis.reduce((s, m) => s + Number(m.preco || 0), 0)
  const total         = precoFinalNum > 0 && precoFinalNum < totalBruto ? precoFinalNum : totalBruto

  return (
    <div className="flex flex-col gap-4">
      {/* Toggle */}
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          {...register('pagamentoDividido')}
          className="accent-[#F5C200] w-4 h-4"
        />
        <span className="text-sm font-medium text-gray-700">Dividir em 2 formas de pagamento</span>
      </label>

      {!dividido ? (
        <>
          <div>
            <label className="label">Forma de pagamento *</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {FORMAS_PAGAMENTO.map(fp => {
                const ativa = watch('formaPagamento') === fp.value
                return (
                  <label
                    key={fp.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all text-sm
                      ${ativa
                        ? 'border-[#F5C200] bg-[#F5C200]/10 font-semibold text-[#111111]'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  >
                    <input type="radio" {...register('formaPagamento')} value={fp.value} className="sr-only" />
                    <span>{fp.icon}</span>
                    <span>{fp.label}</span>
                  </label>
                )
              })}
            </div>
            {errors.formaPagamento && <p className="error-msg">{errors.formaPagamento.message}</p>}
          </div>

          <div>
            <label className="label">Valor pendente na entrega (R$)</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">R$</span>
              <input
                {...register('valorPendenteEntrega')}
                type="text"
                inputMode="numeric"
                className="input pl-9"
                placeholder="0,00"
                onChange={e => {
                  e.target.value = fmtBR(e.target.value)
                  register('valorPendenteEntrega').onChange(e)
                }}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <SlotPagamento titulo="Entrada" prefixo="entrada" />
          <SlotPagamento titulo="Saldo (na entrega)" prefixo="saldo" />
          <ResumoSoma total={total} />
        </>
      )}
    </div>
  )
}

function SlotPagamento({ titulo, prefixo }) {
  const { register, watch } = useFormContext()
  return (
    <div className="border-2 border-gray-200 rounded-xl p-3 bg-gray-50/50">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{titulo}</p>
      <div className="flex flex-col gap-2">
        <div>
          <label className="text-[10px] text-slate-500">Valor (R$)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">R$</span>
            <input
              {...register(`${prefixo}Valor`)}
              type="text"
              inputMode="numeric"
              className="input pl-9"
              placeholder="0,00"
              onChange={e => {
                e.target.value = fmtBR(e.target.value)
                register(`${prefixo}Valor`).onChange(e)
              }}
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Forma</label>
          <div className="grid grid-cols-2 gap-1.5 mt-0.5">
            {FORMAS_PAGAMENTO.map(fp => {
              const ativa = watch(`${prefixo}Forma`) === fp.value
              return (
                <label
                  key={fp.value}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border-2 cursor-pointer text-xs transition-all
                    ${ativa
                      ? 'border-[#F5C200] bg-[#F5C200]/10 font-semibold text-[#111111]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300 bg-white'}`}
                >
                  <input type="radio" {...register(`${prefixo}Forma`)} value={fp.value} className="sr-only" />
                  <span>{fp.icon}</span>
                  <span>{fp.label}</span>
                </label>
              )
            })}
          </div>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Observação (opcional)</label>
          <input
            {...register(`${prefixo}Obs`)}
            type="text"
            className="input"
            placeholder="Ex: cartão 3x sem juros"
          />
        </div>
      </div>
    </div>
  )
}

function ResumoSoma({ total }) {
  const { watch } = useFormContext()
  const soma  = parseBR(watch('entradaValor')) + parseBR(watch('saldoValor'))
  const bate  = Math.abs(soma - total) < 0.01
  const dif   = total - soma
  return (
    <div className={`text-xs px-3 py-2 rounded-lg ${bate ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>
      Soma: <strong>R$ {soma.toFixed(2).replace('.', ',')}</strong>
      {' · '}Total do pedido: <strong>R$ {total.toFixed(2).replace('.', ',')}</strong>
      {' · '}
      {bate ? '✓ bate' : `${dif > 0 ? 'faltam' : 'sobram'} R$ ${Math.abs(dif).toFixed(2).replace('.', ',')}`}
    </div>
  )
}

export function StepCliente() {
  const { register, watch, setValue, formState: { errors } } = useFormContext()
  const tipoDoc    = watch('cliente.tipoDocumento')
  const docRaw     = watch('cliente.documento') || ''
  const prazoPag   = watch('prazoPagamento')
  const cidade     = watch('cliente.cidade') || ''
  const [cepLoading, setCepLoading] = useState(false)

  // Sugestão de frete baseada na cidade
  const FRETE_SUGESTOES = [
    { label: 'João Pessoa',   hint: 'Grátis',     valor: '0',     cidades: ['joão pessoa','joao pessoa'] },
    { label: 'Campina Grande',hint: 'R$ 49,90',   valor: '49,90', cidades: ['campina grande'] },
    { label: 'Natal',         hint: 'R$ 49,90',   valor: '49,90', cidades: ['natal'] },
    { label: 'Recife',        hint: 'R$ 69,90',   valor: '69,90', cidades: ['recife'] },
    { label: 'Olinda',        hint: 'R$ 99,90',   valor: '99,90', cidades: ['olinda'] },
    { label: 'Jaboatão',      hint: 'R$ 99,90',   valor: '99,90', cidades: ['jaboatão','jaboatao'] },
  ]

  const freteLocalAtivo = watch('frete') === '__outro__' || (!FRETE_SUGESTOES.some(s => s.valor === watch('frete')) && watch('frete') !== '' && watch('frete') !== '0' && watch('frete') !== undefined && watch('freteLocal'))

  useEffect(() => {
    const cidadeLower = cidade.toLowerCase().trim()
    for (const s of FRETE_SUGESTOES) {
      if (s.cidades.some(c => cidadeLower.includes(c))) {
        setValue('frete', s.valor)
        setValue('freteLocal', '')
        return
      }
    }
  }, [cidade])

  useEffect(() => {
    if (tipoDoc === 'CPF') setValue('cliente.contribuinteICMS', null)
  }, [tipoDoc, setValue])

  async function buscarCep(cep) {
    setCepLoading(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const data = await res.json()
      if (data.erro !== true) {
        setValue('cliente.rua', data.logradouro || '')
        setValue('cliente.bairro', data.bairro || '')
        setValue('cliente.cidade', data.localidade || '')
        setValue('cliente.uf', data.uf || '')
      }
    } catch {
      // silently fail
    } finally {
      setCepLoading(false)
    }
  }

  function maskDoc(value, tipo) {
    const digits = value.replace(/\D/g, '')
    if (tipo === 'CPF') {
      return digits.slice(0, 11)
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    }
    return digits.slice(0, 14)
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
  }

  function preencherCliente(c) {
    // eslint-disable-next-line no-unused-vars
    const { _origem, ...dados } = c
    c = dados
    setValue('cliente.nome',            c.nome            || '')
    setValue('cliente.contato',         formatTelefone(c.contato))
    setValue('cliente.email',           c.email           || '')
    setValue('cliente.tipoDocumento',   c.tipoDocumento   || 'CPF')
    setValue('cliente.documento',       c.documento       || '')
    setValue('cliente.contribuinteICMS',c.contribuinteICMS|| null)
    setValue('cliente.cep',             c.cep             || '')
    setValue('cliente.rua',             c.rua             || '')
    setValue('cliente.numero',          c.numero          || '')
    setValue('cliente.complemento',     c.complemento     || '')
    setValue('cliente.bairro',          c.bairro          || '')
    setValue('cliente.cidade',          c.cidade          || '')
    setValue('cliente.uf',              c.uf              || '')
  }

  return (
    <div className="space-y-6">

      {/* ── Data da venda ─────────────────────────────────────────────── */}
      <div>
        <Field label="Data da venda *" error={errors.dataVenda}>
          <input
            {...register('dataVenda')}
            type="date"
            className={`input w-48 ${errors.dataVenda ? 'input-error' : ''}`}
          />
        </Field>
      </div>

      {/* ── Dados do cliente ──────────────────────────────────────────── */}
      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
          Dados do cliente
        </h3>

        <BuscaCliente onSelecionar={preencherCliente} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label="Nome completo / Razão social *" error={errors.cliente?.nome}>
              <input {...register('cliente.nome')}
                className={`input ${errors.cliente?.nome ? 'input-error' : ''}`}
                placeholder="Ex: João da Silva" />
            </Field>
          </div>

          <div>
            <label className="label">Tipo de documento *</label>
            <div className="flex gap-3 mt-1">
              {['CPF', 'CNPJ'].map(tipo => (
                <label key={tipo} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" {...register('cliente.tipoDocumento')}
                    value={tipo} className="accent-[#F5C200]" />
                  <span className="text-sm font-medium text-gray-700">{tipo}</span>
                </label>
              ))}
            </div>
          </div>

          <Field label={`${tipoDoc || 'CPF/CNPJ'} *`} error={errors.cliente?.documento}>
            <input
              value={maskDoc(docRaw, tipoDoc || 'CPF')}
              onChange={e => setValue('cliente.documento', e.target.value.replace(/\D/g, ''))}
              className={`input ${errors.cliente?.documento ? 'input-error' : ''}`}
              placeholder={tipoDoc === 'CNPJ' ? '00.000.000/0001-00' : '000.000.000-00'}
              inputMode="numeric"
            />
            <input type="hidden" {...register('cliente.documento')} />
          </Field>

          {tipoDoc === 'CNPJ' && (
            <div className="sm:col-span-2">
              <label className="label">Contribuinte ICMS? *</label>
              <div className="flex gap-4 mt-1">
                {[
                  { value: 'sim', label: 'Sim — Contribuinte ICMS' },
                  { value: 'nao', label: 'Não contribuinte'        },
                ].map(op => (
                  <label key={op.value} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" {...register('cliente.contribuinteICMS')}
                      value={op.value} className="accent-[#F5C200]" />
                    <span className="text-sm font-medium text-gray-700">{op.label}</span>
                  </label>
                ))}
              </div>
              {errors.cliente?.contribuinteICMS && (
                <p className="error-msg">{errors.cliente.contribuinteICMS.message}</p>
              )}
            </div>
          )}

          <Field label="Telefone / WhatsApp *" error={errors.cliente?.contato}>
            <input {...register('cliente.contato', {
                onChange: e => {
                  // Aplica máscara (99) 99999-9999 em tempo real conforme o user digita
                  const v = formatTelefone(e.target.value)
                  setValue('cliente.contato', v, { shouldValidate: false })
                }
              })}
              type="tel"
              maxLength={15}
              className={`input ${errors.cliente?.contato ? 'input-error' : ''}`}
              placeholder="(00) 90000-0000" />
          </Field>

          <Field label="E-mail" error={errors.cliente?.email}>
            <input {...register('cliente.email')} type="email"
              className={`input ${errors.cliente?.email ? 'input-error' : ''}`}
              placeholder="exemplo@email.com" />
          </Field>

          <div className="sm:col-span-2">
            <p className="label">Endereço de entrega *</p>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mt-1">
              <div className="sm:col-span-4">
                <label className="text-xs text-slate-400 mb-1 block">Rua / Avenida</label>
                <input {...register('cliente.rua')}
                  className={`input ${errors.cliente?.rua ? 'input-error' : ''}`}
                  placeholder="Ex: Rua das Flores" />
                {errors.cliente?.rua && <p className="error-msg">{errors.cliente.rua.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-slate-400 mb-1 block">Número</label>
                <input {...register('cliente.numero')}
                  className={`input ${errors.cliente?.numero ? 'input-error' : ''}`}
                  placeholder="Ex: 123" />
                {errors.cliente?.numero && <p className="error-msg">{errors.cliente.numero.message}</p>}
              </div>
              <div className="sm:col-span-3">
                <label className="text-xs text-slate-400 mb-1 block">Bairro</label>
                <input {...register('cliente.bairro')}
                  className={`input ${errors.cliente?.bairro ? 'input-error' : ''}`}
                  placeholder="Ex: Centro" />
                {errors.cliente?.bairro && <p className="error-msg">{errors.cliente.bairro.message}</p>}
              </div>
              <div className="sm:col-span-3">
                <label className="text-xs text-slate-400 mb-1 block">CEP</label>
                <div className="relative">
                  <input {...register('cliente.cep')}
                    className="input pr-8"
                    placeholder="00000-000"
                    inputMode="numeric"
                    maxLength={9}
                    onChange={e => {
                      const d = e.target.value.replace(/\D/g, '').slice(0, 8)
                      e.target.value = d.length > 5 ? `${d.slice(0,5)}-${d.slice(5)}` : d
                      register('cliente.cep').onChange(e)
                      if (d.length === 8) buscarCep(d)
                    }}
                  />
                  {cepLoading && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                      <span className="block w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-4">
                <label className="text-xs text-slate-400 mb-1 block">Cidade</label>
                <input {...register('cliente.cidade')}
                  className={`input ${errors.cliente?.cidade ? 'input-error' : ''}`}
                  placeholder="Ex: João Pessoa" />
                {errors.cliente?.cidade && <p className="error-msg">{errors.cliente.cidade.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-slate-400 mb-1 block">UF</label>
                <select {...register('cliente.uf')} className="input">
                  <option value="">--</option>
                  {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-6">
                <label className="text-xs text-slate-400 mb-1 block">
                  Complemento <span className="text-red-400">*</span>
                  <span className="text-slate-300 font-normal ml-1">(Casa, Apto, etc.)</span>
                </label>
                {/* Botões rápidos */}
                <div className="flex gap-2 mb-2">
                  <button type="button"
                    onClick={() => setValue('cliente.complemento', 'Casa')}
                    className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors
                      ${watch('cliente.complemento') === 'Casa'
                        ? 'bg-[#111111] text-white border-[#111111]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                  >
                    Casa
                  </button>
                  {/* Botão Apto preenche um template no campo */}
                  <button type="button"
                    onClick={() => {
                      const atual = watch('cliente.complemento') || ''
                      if (!atual.startsWith('Apto')) setValue('cliente.complemento', 'Apto ')
                    }}
                    className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors
                      ${(watch('cliente.complemento') || '').startsWith('Apto')
                        ? 'bg-[#111111] text-white border-[#111111]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                  >
                    Apto
                  </button>
                </div>
                <input {...register('cliente.complemento')}
                  className={`input ${errors.cliente?.complemento ? 'input-error' : ''}`}
                  placeholder="Ex: Casa, Apto 302, Bloco B, Casa dos fundos..." />
                {errors.cliente?.complemento && <p className="error-msg">{errors.cliente.complemento.message}</p>}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Informações comerciais ────────────────────────────────────── */}
      <div className="border-t border-gray-100 pt-5">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
          Informações comerciais
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          {/* Vendedora */}
          <div>
            <label className="label">Vendedora *</label>
            <div className="flex gap-3 mt-1">
              {VENDEDORAS.map(v => {
                const ativa = watch('vendedora') === v
                return (
                  <label
                    key={v}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all flex-1 justify-center
                      ${ativa
                        ? 'border-[#F5C200] bg-[#F5C200]/10 font-semibold text-[#111111]'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  >
                    <input type="radio" {...register('vendedora')} value={v} className="sr-only" />
                    <span className="text-sm">{v}</span>
                  </label>
                )
              })}
            </div>
            {errors.vendedora && <p className="error-msg">{errors.vendedora.message}</p>}
          </div>

          {/* Forma de pagamento — único ou dividido em 2 formas */}
          <PagamentoBloco />


          {/* Frete */}
          <div className="sm:col-span-2">
            <label className="label">
              Frete <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2 mt-1 mb-2">
              {FRETE_SUGESTOES.map(s => {
                const ativo = watch('frete') === s.valor && !watch('freteLocal')
                return (
                  <button key={s.label} type="button"
                    onClick={() => { setValue('frete', s.valor); setValue('freteLocal', '') }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all
                      ${ativo
                        ? 'border-[#F5C200] bg-[#F5C200]/10 text-[#111111]'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  >
                    {s.label}
                    <span className={`text-xs ${ativo ? 'text-[#CC8800]' : 'text-slate-400'}`}>{s.hint}</span>
                  </button>
                )
              })}
              {/* Outro local */}
              <button type="button"
                onClick={() => { setValue('freteLocal', ' '); setValue('frete', '') }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all
                  ${watch('freteLocal')?.trim()
                    ? 'border-[#F5C200] bg-[#F5C200]/10 text-[#111111]'
                    : 'border-dashed border-gray-300 text-gray-500 hover:border-gray-400'}`}
              >
                + Outro local
              </button>
            </div>

            {/* Campo local específico */}
            {watch('freteLocal') !== '' && watch('freteLocal') !== undefined && watch('freteLocal') !== null && (
              <input
                {...register('freteLocal')}
                type="text"
                className="input mb-2"
                placeholder="Nome do local (ex: Bananeiras, Conde, Alhandra…)"
                autoFocus
              />
            )}

            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">R$</span>
              <input
                {...register('frete')}
                type="text"
                inputMode="numeric"
                className={`input pl-9 ${errors.frete ? 'input-error' : ''}`}
                placeholder="0,00 (digite 0 se for grátis)"
                onChange={e => {
                  const limpo = e.target.value.replace(/[^\d,]/g, '')
                  const [int, ...dec] = limpo.split(',')
                  const fmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (dec.length ? ',' + dec.join('').slice(0,2) : '')
                  e.target.value = fmt
                  register('frete').onChange(e)
                }}
              />
            </div>
            {errors.frete && <p className="error-msg mt-1">{errors.frete.message}</p>}
          </div>

          {/* Prazo de pagamento */}
          <div className="sm:col-span-2">
            <label className="label">Prazo de pagamento *</label>
            <div className="flex flex-col gap-2 mt-1">
              {PRAZOS_PAGAMENTO.map(pp => {
                const ativo = prazoPag === pp.value
                return (
                  <label
                    key={pp.value}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all
                      ${ativo
                        ? 'border-[#F5C200] bg-[#F5C200]/10'
                        : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <input type="radio" {...register('prazoPagamento')} value={pp.value} className="accent-[#F5C200]" />
                    <span className={`text-sm ${ativo ? 'font-semibold text-[#111111]' : 'text-gray-700'}`}>
                      {pp.label}
                    </span>
                  </label>
                )
              })}
            </div>
            {prazoPag === 'custom' && (
              <input
                {...register('prazoPagamentoCustom')}
                className="input mt-2"
                placeholder="Ex: 30% no pedido, 70% na entrega..."
              />
            )}
            {errors.prazoPagamento && <p className="error-msg">{errors.prazoPagamento.message}</p>}
          </div>

        </div>
      </div>
    </div>
  )
}
